// src/helpers/foodFilterApplier.js
// Apply food filter to list of foods

/**
 * Check if food has any of the specified tags
 */
function hasAnyTag(food, tags) {
  if (!tags || tags.length === 0) return false;
  if (!food.tagsV2 || food.tagsV2.length === 0) return false;
  return tags.some((tag) => food.tagsV2.includes(tag));
}

/**
 * Check if food has all of the specified tags
 */
function hasAllTags(food, tags) {
  if (!tags || tags.length === 0) return true;
  if (!food.tagsV2 || food.tagsV2.length === 0) return false;
  return tags.every((tag) => food.tagsV2.includes(tag));
}

/**
 * Calculate priority score based on matching prioritize tags
 */
function calculatePriorityScore(food, prioritizeTags) {
  if (!prioritizeTags || prioritizeTags.length === 0) return 0;
  if (!food.tagsV2 || food.tagsV2.length === 0) return 0;

  // Count how many priority tags this food matches
  return prioritizeTags.filter((tag) => food.tagsV2.includes(tag)).length;
}

/**
 * Apply food filter to a list of foods
 * @param {Array} foods - Array of food objects with tagsV2
 * @param {Object} filter - Filter object from buildFoodFilterFromProfile
 * @returns {Array} Filtered and sorted array of foods
 */
export function applyFoodFilter(foods, filter) {
  if (!foods || foods.length === 0) return [];
  if (!filter) return foods;

  console.log('[applyFoodFilter] Starting with', foods.length, 'foods');
  console.log('[applyFoodFilter] Filter:', {
    excludedTags: filter.excludedTags,
    requiredTags: filter.requiredTags,
    avoidTags: filter.avoidTags,
    prioritizeTags: filter.prioritizeTags,
    forbiddenKeywords: filter.forbiddenKeywords,
    dietStyle: filter.dietStyle,
    hasPreferences: filter.hasPreferences,
  });

  // Step 1: Hard filter - remove excluded items and require certain tags
  let filtered = foods.filter((food) => {
    // First check: name-based filtering (fallback for prepared dishes without ingredient tags)
    if (filter.forbiddenKeywords && filter.forbiddenKeywords.length > 0) {
      const foodName = (food.name || '').toLowerCase();
      const hasForbiddenKeyword = filter.forbiddenKeywords.some((keyword) =>
        foodName.includes(keyword.toLowerCase())
      );
      if (hasForbiddenKeyword) {
        console.log(
          '[applyFoodFilter] EXCLUDED by forbidden keyword in name:',
          food.name
        );
        return false;
      }
    }

    // Second check: category tag-based filtering
    // Exclude if food has any excluded tags
    if (hasAnyTag(food, filter.excludedTags)) {
      console.log(
        '[applyFoodFilter] EXCLUDED:',
        food.name,
        'has excluded tag from',
        food.tagsV2
      );
      return false;
    }

    // Require food to have all required tags
    if (!hasAllTags(food, filter.requiredTags)) {
      console.log(
        '[applyFoodFilter] EXCLUDED:',
        food.name,
        'missing required tags'
      );
      return false;
    }

    return true;
  });

  console.log('[applyFoodFilter] After hard filter:', filtered.length, 'foods remain');

  // Step 2: Soft filter - mark avoided items and calculate priority
  filtered = filtered.map((food) => ({
    ...food,
    isAvoided: hasAnyTag(food, filter.avoidTags),
    priorityScore: calculatePriorityScore(food, filter.prioritizeTags),
  }));

  // Step 3: Sort by priority
  filtered.sort((a, b) => {
    // Avoided items always go to bottom
    if (a.isAvoided !== b.isAvoided) {
      return a.isAvoided ? 1 : -1;
    }

    // Otherwise sort by priority score (higher first)
    if (a.priorityScore !== b.priorityScore) {
      return b.priorityScore - a.priorityScore;
    }

    // Keep original order if same priority
    return 0;
  });

  console.log('[applyFoodFilter] Final result:', filtered.length, 'foods');
  if (filtered.length > 0) {
    console.log(
      '[applyFoodFilter] First 3:',
      filtered.slice(0, 3).map((f) => f.name)
    );
  }

  return filtered;
}

/**
 * Get active filter summary for display
 * @param {Object} filter - Filter object
 * @param {string} category - Selected category
 * @returns {Array} Array of {label, isFromProfile} objects
 */
export function getActiveFilterSummary(filter, category) {
  const summary = [];

  // Add category if not "all"
  if (category && category !== 'all') {
    summary.push({
      label: getCategoryLabel(category),
      isFromProfile: false,
    });
  }

  // Add diet style if set
  if (filter.dietStyle && filter.dietStyle !== 'none') {
    summary.push({
      label: getDietStyleLabel(filter.dietStyle),
      isFromProfile: true,
    });
  }

  // Add allergy-based filters
  // (Would need to track which specific allergies are active - simplified here)

  return summary;
}

/**
 * Get category label for display
 */
function getCategoryLabel(categoryId) {
  const labels = {
    bread_bakery: 'Bread & Bakery',
    grain_cereal: 'Grains & Cereals',
    dairy: 'Dairy',
    meat: 'Meat',
    poultry: 'Poultry',
    organ_meat: 'Organ Meats',
    fish_seafood: 'Fish & Seafood',
    // ... add more as needed
  };
  return labels[categoryId] || categoryId;
}

/**
 * Get diet style label
 */
function getDietStyleLabel(dietStyle) {
  const labels = {
    vegan: 'Vegan',
    vegetarian: 'Vegetarian',
    pescatarian: 'Pescatarian',
    noRedMeat: 'No red meat',
  };
  return labels[dietStyle] || dietStyle;
}
